import {Injectable} from '@angular/core';
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {KlinikaxpPaymentRequiredReservation} from "../models/klinikaxp-payment-required-reservation.model";
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {Observable} from "rxjs";
import {
  KlinikaXpPaymentRequiredReservationDgSearchResults
} from "../models/datagrid-search-results/klinika-xp-payment-required-reservation.dg-search.results";

/**
 * Serwis zarządzający typem rezerwacji pochodzących z KlinikiXP,
 * które wymagają uiszczenia przedpłaty online do określonego czasu.
 * Obsługuje generowanie linków do płatności, wysyłkę SMS oraz modyfikację warunków rezerwacji.
 */
@Injectable({
  providedIn: 'root'
})
export class KlinikaxpPaymentRequiredReservationService implements DataGridServiceInterface<Observable<KlinikaXpPaymentRequiredReservationDgSearchResults>> {

  constructor(
    private apiService: ApiService,
    public commonUtils: CommonUtils,
  ) {
  }

  /**
   * Pobiera paginowaną listę rezerwacji oczekujących na płatność dla widoku DataGrid.
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<KlinikaXpPaymentRequiredReservationDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/kxp-payment-required-reservations/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        console.log(vetineoApiResponse.response);
        return new KlinikaXpPaymentRequiredReservationDgSearchResults(
          vetineoApiResponse.response.total,
          this.commonUtils.bestTypedArrayCopyEver(new KlinikaxpPaymentRequiredReservation(), [], vetineoApiResponse.response.results),
          vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wykonuje filtrowanie/wyszukiwanie po wizytach wymagających przedpłaty.
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<KlinikaXpPaymentRequiredReservationDgSearchResults> {
      const data = {
        limit: limit,
        offset: offset,
        searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
        sortOrder: sortOrder,
        orderBy: orderBy,
        showDeletedObjects: showDeletedObjects
      };
      return this.apiService.post<VetineoApiResponse>(`/dashboard/kxp-payment-required-reservations/search`, data).pipe(map(vetineoApiResponse => {
        if (vetineoApiResponse.ok) {
          return new KlinikaXpPaymentRequiredReservationDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new KlinikaxpPaymentRequiredReservation(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
        } else {
          return undefined;
        }
      }));
    }

  /**
   * Pobiera najnowsze, niedawno zsynchronizowane z KlinikąXP rezerwacje dla danej kliniki.
   *
   * @param vetcodeId Identyfikator kliniki.
   * @returns Observable emitujący tablicę nowych, jeszcze nieobsłużonych rezerwacji.
   */
  getNewest(vetcodeId: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/ref-appt/${vetcodeId}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new KlinikaxpPaymentRequiredReservation(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }


  /**
   * Zapisuje nowo skonfigurowany link do płatności dla danej rezerwacji w systemie.
   * Ustala np. kwotę przedpłaty oraz ostateczny czas na zapłatę.
   */
  saveKlinikaXpPaymentRequredReservationInfo(klinikaxpPaymentRequiredReservation: KlinikaxpPaymentRequiredReservation): Observable<KlinikaxpPaymentRequiredReservation> {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/klinika-appt`, klinikaxpPaymentRequiredReservation).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new KlinikaxpPaymentRequiredReservation(), vetineoApiResponse.response);
      } else {
        throw Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Pobiera starsze rezerwacje pobrane z KlinikiXP.
   */
  getPrevious(vetcodeId: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/ref-appt/prev/${vetcodeId}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new KlinikaxpPaymentRequiredReservation(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Odpytuje serwer o aktualny czas.
   */
  getServerTime() {
    return this.apiService.get<VetineoApiResponse>('/dashboard/get-server-time').pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response.currentServerTime;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Anuluje/wycofuje wysłany wcześniej link do płatności z powodu określonej przyczyny.
   *
   * @param obj Obiekt zawierający UUID rezerwacji i powód usunięcia.
   */
  removePaymentLink(obj: { reason: any; uuid: string }) {
    return this.apiService.post<VetineoApiResponse>('/dashboard/cancel-link', obj).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new KlinikaxpPaymentRequiredReservation(), vetineoApiResponse.response);
      } else {
        throw Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Zmienia (przesuwa) ostateczny termin (Deadline), do którego pacjent musi opłacić przedpłatę,
   * z opcją natychmiastowego powiadomienia pacjenta SMS-em o wydłużeniu tego czasu.
   *
   * @param uuid Identyfikator rezerwacji.
   * @param finalPaymentDate Nowa data krańcowa.
   * @param sendSms Flaga decydująca o wysyłce powiadomienia.
   */
  changeKlinikaPaymentRequiredReservationDate(uuid: string, finalPaymentDate: string, sendSms: Boolean) {
    return this.apiService.post<VetineoApiResponse>('/dashboard/change-link-time', {'uuid':uuid, 'newDate': finalPaymentDate, 'sendSMS': sendSms}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new KlinikaxpPaymentRequiredReservation(), vetineoApiResponse.response);
      } else {
        throw Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Ręcznie wymusza ponowną wysyłkę wiadomości SMS z linkiem do płatności do pacjenta.
   */
  sendLinkBySMS(klinikaxpPaymentRequiredReservation: KlinikaxpPaymentRequiredReservation) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/send-kxp-link-sms/${klinikaxpPaymentRequiredReservation.uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new KlinikaxpPaymentRequiredReservation(), [], vetineoApiResponse.response);
      } else {
        throw Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Aktualizuje dane kontaktowe pobrane z KlinikiXP
   * i natychmiast wysyła nowy link SMS.
   */
  updateContactDataAndSendLink(klinikaxpPaymentRequiredReservation: KlinikaxpPaymentRequiredReservation) {
    return this.apiService.put<VetineoApiResponse>(`/dashboard/klinika-appt/data-update`, klinikaxpPaymentRequiredReservation).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new KlinikaxpPaymentRequiredReservation(), vetineoApiResponse.response);
      } else {
        throw Error(vetineoApiResponse.errors.main);
      }
    }));
  }
}
